import { Checkbox } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import Modal from "react-responsive-modal";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import * as Yup from "yup";
import { format, isAfter } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";

import { ptBR } from "date-fns/locale";

import getValidationErrors from "../../utils/getValidationError";
import {
  Content,
  TitleContainer,
  ConsultantsAndOptionContainer,
  ConsultantsContainer,
  ConsultantContent,
  ConsultantsAndDatesContainer,
  DatesContainer,
  ButtonsContainer,
} from "./styles";

interface IModalVisitsPerConsultants {
  open: boolean;
  functionToCloseModal(): void;
}

export default function ModalActivePropertiesPerPeriod({
  open,
  functionToCloseModal,
}: IModalVisitsPerConsultants) {
  const { dateToExpires, access_token } = useAuth();
  const { addToast } = useToast();
  const [cities, setCities] = useState<string[]>([]);
  const [initialDate, setInitialDate] = useState<Date>(new Date());
  const [finalDate, setFinalDate] = useState<Date>(new Date());
  const [loadingExcelRequest, setLoadingExcelRequest] = useState(false);

  const locales = useMemo(
    () => [
      { value: "Recife", label: "Recife" },
      { value: "Fortaleza", label: "Fortaleza" },
      { value: "Aquiraz", label: "Aquiraz" },
      { value: "Caucaia", label: "Caucaia" },
      { value: "Eusébio", label: "Eusébio" },
      { value: "Maceió", label: "Maceió" },
      { value: "Salvador", label: "Salvador" },
      { value: "Teresina", label: "Teresina" },
    ],
    []
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    if (event.target.checked) {
      setCities((prevCities) => [...prevCities, value]);
    } else {
      setCities((prevCities) => prevCities.filter((city) => city !== value));
    }
  };

  const handleExcelDownloadReport = useCallback(async () => {
    setLoadingExcelRequest(true);

    const data = {
      cities: cities.join(","),
      initial_date: format(initialDate, "yyyy-MM-dd"),
      final_date: format(finalDate, "yyyy-MM-dd"),
    };

    console.log("Dados enviados para a API:", data);

    if (isAfter(initialDate, finalDate)) {
      addToast({
        title: "Coloque datas válidas!",
        type: "error",
      });
      setLoadingExcelRequest(false);
      return;
    }

    try {
      const schema = Yup.object().shape({
        cities: Yup.string().required("Selecione uma cidade!"),
        initial_date: Yup.date().required("Digite uma data inicial!"),
        final_date: Yup.date()
          .required("Digite uma data final!")
          .min(
            Yup.ref("initial_date"),
            "A data final deve ser posterior à data inicial!"
          ),
      });

      await schema.validate(data, { abortEarly: false });

      const token = await refreshToken(dateToExpires, access_token);

      if (!token && !access_token) {
        addToast({
          title: "Token inválido ou expirado!",
          type: "error",
        });
        setLoadingExcelRequest(false);
        return;
      }

      api.defaults.headers.authorization = `Bearer ${token || access_token}`;

      const response = await api.post(
        "/api/adm/reports/activatePropertiesByDate",
        data,
        {
          responseType: "blob",
        }
      );

      console.log("Resposta da API:", response);

      // Verifica se a resposta é um Blob válido
      if (response.data instanceof Blob) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Imóveis Ativos por Período.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        addToast({
          title: "Erro ao gerar o relatório.",
          type: "error",
        });
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });
      } else {
        addToast({
          title: "Erro ao gerar o relatório.",
          type: "error",
        });
      }
      console.error(err);
    }

    setLoadingExcelRequest(false);
  }, [dateToExpires, access_token, cities, initialDate, finalDate, addToast]);

  return (
    <Modal
      open={open}
      onClose={functionToCloseModal}
      classNames={{ modal: "modalVisitsPerConsultant" }}
      closeIcon={<FiX size={18} color="#000" />}
    >
      <Content>
        <TitleContainer>
          <h3>IMÓVEIS ATIVOS POR PERÍODO</h3>
        </TitleContainer>

        <ConsultantsAndDatesContainer>
          <ConsultantsAndOptionContainer>
            <p>Região: </p>
            <ConsultantsContainer>
              {locales.map((localFinded) => (
                <ConsultantContent key={localFinded.value}>
                  <Checkbox
                    checked={cities.includes(localFinded.value)}
                    onChange={(e) => handleChange(e, localFinded.value)}
                  />
                  <p>{localFinded.label}</p>
                </ConsultantContent>
              ))}
            </ConsultantsContainer>
          </ConsultantsAndOptionContainer>

          <DatesContainer>
            <p>Data inicial:</p>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                value={initialDate}
                onChange={(e) => setInitialDate(e as Date)}
                format="dd/MM/yyyy"
              />
            </LocalizationProvider>

            <p>Data final:</p>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                value={finalDate}
                onChange={(e) => setFinalDate(e as Date)}
                format="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </DatesContainer>
        </ConsultantsAndDatesContainer>
        <ButtonsContainer>
          <button
            disabled={loadingExcelRequest}
            onClick={handleExcelDownloadReport}
          >
            {loadingExcelRequest ? "Carregando..." : "Excel"}
          </button>
        </ButtonsContainer>
      </Content>
    </Modal>
  );
}
